import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IconTag, IconPalette, IconCheck, IconX } from "@tabler/icons-react";

// Schema de validação
const tagSchema = yup.object().shape({
  name: yup
    .string()
    .required("Nome é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(50, "Nome não pode ter mais de 50 caracteres")
    .matches(
      /^[a-zA-Z0-9\s\-_\.]+$/,
      "Nome pode conter apenas letras, números, espaços, hífens, underscores e pontos"
    ),
  description: yup
    .string()
    .max(200, "Descrição não pode ter mais de 200 caracteres"),
  color: yup
    .string()
    .required("Cor é obrigatória")
    .matches(/^#[0-9A-Fa-f]{6}$/, "Cor deve estar no formato hexadecimal"),
  category: yup
    .string()
    .required("Categoria é obrigatória")
    .oneOf(
      ["client", "contact", "appointment", "chat", "general"],
      "Categoria inválida"
    ),
  is_shared: yup.boolean(),
});

// Cores pré-definidas
const PREDEFINED_COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#96CEB4",
  "#FFEAA7",
  "#DDA0DD",
  "#FFB347",
  "#F8C8DC",
  "#D3D3D3",
  "#98FB98",
  "#FF9FF3",
  "#54A0FF",
  "#5F27CD",
  "#00D2D3",
  "#FF9F43",
  "#FC5C65",
  "#26DE81",
  "#FD79A8",
  "#FDCB6E",
  "#000000"
];

// Categorias disponíveis
const CATEGORIES = [
  // { value: "service", label: "Atendimento", icon: "" },
  { value: "appointment", label: "Agendamento", icon: "" },
  { value: "client", label: "Cliente", icon: "" },
  { value: "contact", label: "Contato", icon: "" },
  { value: "chat", label: "Chat", icon: "" },
  // { value: "funnel", label: "CRM", icon: "" },
  { value: "general", label: "Geral", icon: "" },
];

const TagModal = ({
  show,
  onHide,
  onSave,
  tag = null,
  loading = false,
  error = null,
}) => {
  const isEditing = !!tag;
  const [selectedColor, setSelectedColor] = useState("#45B7D1");

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(tagSchema),
    defaultValues: {
      name: "",
      description: "",
      color: "#45B7D1",
      category: "general",
      is_shared: false,
    },
    mode: "onChange",
  });

  const watchedValues = watch();

  // Resetar form quando modal abrir/fechar ou tag mudar
  useEffect(() => {
    if (show) {
      if (isEditing && tag) {
        reset({
          name: tag.name || "",
          description: tag.description || "",
          color: tag.color || "#45B7D1",
          category: tag.category || "general",
          is_shared: tag.is_shared || false,
        });
        setSelectedColor(tag.color || "#45B7D1");
      } else {
        reset({
          name: "",
          description: "",
          color: "#45B7D1",
          category: "general",
          is_shared: false,
        });
        setSelectedColor("#45B7D1");
      }
    }
  }, [show, isEditing, tag, reset]);

  // Atualizar cor selecionada quando mudada no form
  useEffect(() => {
    if (watchedValues.color) {
      setSelectedColor(watchedValues.color);
    }
  }, [watchedValues.color]);

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setValue("color", color, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    try {
      await onSave(data);
      onHide();
    } catch (error) {
      // Erro será tratado pelo componente pai
      console.error("Erro ao salvar tag:", error);
    }
  };

  const getCategoryIcon = (category) => {
    const categoryObj = CATEGORIES.find((cat) => cat.value === category);
    return categoryObj ? categoryObj.icon : "🏷️";
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="tag-modal"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title className="d-flex align-items-center gap-2">
          <IconTag size={24} />
          {isEditing ? "Editar Tag" : "Nova Tag"}
        </Modal.Title>
        <IconX
          size={20}
          color="var(--secondary)"
          style={{ cursor: "pointer" }}
          onClick={onHide}
        />
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            <strong>Erro:</strong> {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Nome da Tag */}
          <div className="form-group">
            <Form.Label>
              Nome da Tag <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Digite o nome da tag"
                  isInvalid={!!errors.name}
                  autoFocus={!isEditing}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </div>

          {/* Descrição */}
          <div className="form-group">
            <Form.Label>Descrição</Form.Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  as="textarea"
                  rows={3}
                  placeholder="Descrição opcional da tag"
                  isInvalid={!!errors.description}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.description?.message}
            </Form.Control.Feedback>
          </div>

          {/* Categoria */}
          <div className="form-group category-section">
            <Form.Label>
              Categoria <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Form.Select {...field} isInvalid={!!errors.category}>
                  {CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.icon} {category.label}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.category?.message}
            </Form.Control.Feedback>
          </div>

          {/* Seletor de Cor */}
          <div className="color-picker-section">
            <Form.Label className="d-flex align-items-center gap-2">
              <IconPalette size={16} />
              Cor <span className="text-danger">*</span>
            </Form.Label>

            <div className="color-options">
              {PREDEFINED_COLORS.map((color) => (
                <div
                  key={color}
                  className={`color-option ${
                    selectedColor === color ? "selected" : ""
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorSelect(color)}
                  title={color}
                >
                  {selectedColor === color && (
                    <IconCheck
                      size={16}
                      color="#fff"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        filter: "drop-shadow(0 0 2px rgba(0,0,0,0.5))",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Input de cor customizada */}
            <div className="mt-3">
              <Form.Label className="small text-muted">
                Ou escolha uma cor personalizada:
              </Form.Label>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="color"
                    style={{ width: "60px", height: "40px" }}
                    onChange={(e) => {
                      field.onChange(e);
                      setSelectedColor(e.target.value);
                    }}
                  />
                )}
              />
            </div>

            {errors.color && (
              <div className="text-danger small mt-1">
                {errors.color.message}
              </div>
            )}
          </div>

          {/* Compartilhamento */}
          <Form.Check
            type="checkbox"
            id="is_shared"
            label="Compartilhar esta tag com toda a equipe"
            className="form-check"
          >
            <Controller
              name="is_shared"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Form.Check.Input
                  type="checkbox"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
            <Form.Check.Label>
              Compartilhar esta tag com toda a equipe
            </Form.Check.Label>
          </Form.Check>

          {/* Preview da Tag */}
          {watchedValues.name && (
            <div className="tag-preview">
              <div className="tag-preview-label">Pré-visualização:</div>
              <div className="tag-preview-item">
                <div
                  className="tag-preview-color"
                  style={{ backgroundColor: selectedColor }}
                />
                <span className="tag-preview-name">
                  {getCategoryIcon(watchedValues.category)} {watchedValues.name}
                </span>
                {watchedValues.is_shared && (
                  <small className="text-success">• Compartilhada</small>
                )}
              </div>
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={loading || !isValid}
          className="d-flex align-items-center gap-2"
        >
          {loading && <Spinner animation="border" size="sm" />}
          {isEditing ? "Salvar Alterações" : "Criar Tag"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TagModal;
